<script setup>
import { ref, computed } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import ModalBox from './ModalBox.vue'
import FormInput from '../Atoms/FormInput.vue'
import FormButton from '../Atoms/FormButton.vue'
import FormCheckbox from '../Atoms/FormCheckbox.vue'
import IconBase from '../icon/IconBase.vue'
import IconClose from '../icon/IconClose.vue'
import Badge from '../Badge.vue'

const props = defineProps({
	streamPlatforms: {
		type: Array,
		required: true,
	},
	selectedOptions: {
		type: Array,
		default: () => [],
	},
	groupName: {
		type: String,
		default: '',
	},
})

const emit = defineEmits(['update:selectedOptions', 'update:groupName', 'create-clicked', 'cancel-clicked'])
const { notify } = useNotification()

const visible = ref(false)
const name = ref('')
const selected = ref([])

const open = () => {
	name.value = ''
	selected.value = []
	visible.value = true
}
const close = () => { visible.value = false }
const toggleSidebar = () => { visible.value ? close() : open() }

const toggleSelection = (id) => {
	const i = selected.value.indexOf(id)
	if (i > -1) selected.value.splice(i, 1)
	else selected.value.push(id)
	emit('update:selectedOptions', [...selected.value])
}

const onName = (val) => {
	name.value = val
	emit('update:groupName', val)
}

const canCreate = computed(() => name.value.trim() !== '')

const selectedLabel = computed(() => {
	const c = selected.value.length
	if (!c) return 'None selected'
	return c === 1 ? '1 selected' : `${c} selected`
})

const handleCreate = () => {
	if (!canCreate.value) {
		notify({ group: 'error', text: 'Please enter a group name.' })
		return
	}
	emit('create-clicked', { selected: [...selected.value], name: name.value.trim() })
	close()
}

const cancel = () => {
	emit('cancel-clicked')
	close()
}

defineExpose({ toggleSidebar })
</script>

<template>
	<ModalBox v-model="visible" size="md" header-class="!hidden" wrapper-classes="lg:h-auto lg:max-h-none" @close="cancel">
		<div class="p-4">
			<div class="flex items-start justify-between gap-3 mb-4">
				<div class="min-w-0">
					<h3 class="text-lg font-semibold text-white mb-0.5">New group</h3>
					<p class="text-tiny text-surface-7 mb-0">Bundle destinations so you can toggle them on or off together.</p>
				</div>
				<FormButton type="link" isIcon @click="cancel" class="shrink-0 -mt-1">
					<icon-base>
						<icon-close />
					</icon-base>
				</FormButton>
			</div>

			<label class="block mb-1 text-sm text-surface-8">Group name</label>
			<FormInput :modelValue="name" @update:modelValue="onName" placeholder="e.g. Social platforms" class="mb-4" />

			<div class="flex items-center justify-between mb-1">
				<label class="text-sm text-surface-8">Select destinations <span class="text-surface-6">(optional)</span></label>
				<span class="text-tiny text-surface-7">{{ selectedLabel }}</span>
			</div>
			<div class="bg-surface-1 rounded-lg p-1 max-h-[280px] overflow-y-auto">
				<div v-if="streamPlatforms.length === 0" class="text-center py-8 text-sm text-surface-6">
					No destinations to group yet.
				</div>
				<FormCheckbox v-for="platform in streamPlatforms" :key="platform._id"
					:name="'group-platform-' + platform._id" :input-value="platform._id"
					class="!gap-3 px-3 py-2 rounded-md hover:bg-surface-3 transition-colors"
					:model-value="selected.includes(platform._id)" @change="toggleSelection(platform._id)">
					<template #label>
						<div class="flex items-center gap-2 min-w-0">
							<div class="min-w-0 grow">
								<div class="text-sm text-surface-9 truncate">{{ platform.editorName }}</div>
								<div class="text-tiny text-surface-7 truncate">{{ platform.server }}</div>
							</div>
							<Badge v-if="platform.isGuest" class="shrink-0">Guest</Badge>
							<Badge v-else-if="platform.template !== 'custom'" class="shrink-0" :title="platform.template" />
						</div>
					</template>
				</FormCheckbox>
			</div>

			<div class="flex items-center gap-2 mt-5">
				<FormButton type="secondary" class="grow justify-center" @click="cancel">Cancel</FormButton>
				<FormButton type="main" class="grow justify-center" :disabled="!canCreate" @click="handleCreate">
					Create group
				</FormButton>
			</div>
		</div>
	</ModalBox>
</template>
